'use client'

import { CircleCheck, CircleX, CircleMinus, CircleDashed } from 'lucide-react'

interface BotAnalysis {
  bot: string
  category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
  hasExplicitRule: boolean
  matchedUserAgent: string | null
  status: 'allowed' | 'blocked' | 'partial' | 'no-rule'
  allowedPaths: string[]
  disallowedPaths: string[]
  crawlDelay: number | null
  description: string
}

interface BotRulesTableProps {
  botAnalysis: BotAnalysis[]
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  search: { label: 'Search', color: '#008dcc' },
  'ai-browse': { label: 'AI Browse', color: '#8b5cf6' },
  'ai-train': { label: 'AI Train', color: '#FDC304' },
  seo: { label: 'SEO', color: '#F90B8A' },
}

const STATUS_CONFIG: Record<
  string,
  { icon: React.ReactNode; label: string; color: string }
> = {
  allowed: {
    icon: <CircleCheck size={16} />,
    label: 'Allowed',
    color: '#22c55e',
  },
  blocked: {
    icon: <CircleX size={16} />,
    label: 'Blocked',
    color: '#F90B8A',
  },
  partial: {
    icon: <CircleMinus size={16} />,
    label: 'Partial',
    color: '#FDC304',
  },
  'no-rule': {
    icon: <CircleDashed size={16} />,
    label: 'No Rule',
    color: '#6b7280',
  },
}

const CATEGORY_ORDER: BotAnalysis['category'][] = [
  'search',
  'ai-browse',
  'ai-train',
  'seo',
]

export default function BotRulesTable({ botAnalysis }: BotRulesTableProps) {
  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    bots: botAnalysis.filter(b => b.category === cat),
  })).filter(g => g.bots.length > 0)

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <h2 className='text-lg font-semibold text-foreground mb-4'>
        Bot Directive Analysis
      </h2>
      <p className='text-sm text-muted-foreground mb-6'>
        How each bot is handled by your robots.txt directives.
      </p>

      <div className='space-y-6'>
        {grouped.map(group => {
          const catConfig = CATEGORY_LABELS[group.category]
          return (
            <div key={group.category}>
              <div className='flex items-center gap-2 mb-3'>
                <span
                  className='text-[10px] font-semibold uppercase rounded-full px-2.5 py-0.5'
                  style={{
                    backgroundColor: `${catConfig.color}15`,
                    color: catConfig.color,
                  }}
                >
                  {catConfig.label}
                </span>
              </div>

              <div className='space-y-2'>
                {group.bots.map(bot => {
                  const statusConfig = STATUS_CONFIG[bot.status]
                  return (
                    <div
                      key={bot.bot}
                      className='bg-background border border-border/60 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center gap-3'
                    >
                      {/* Bot name + description */}
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='text-sm font-semibold text-foreground'>
                            {bot.bot}
                          </span>
                          {bot.hasExplicitRule && (
                            <span className='text-[9px] font-medium uppercase rounded px-1.5 py-0.5 bg-muted text-muted-foreground'>
                              Explicit
                            </span>
                          )}
                        </div>
                        <p className='text-xs text-muted-foreground mt-0.5'>
                          {bot.description}
                        </p>
                      </div>

                      {/* Status */}
                      <div className='flex items-center gap-4 shrink-0'>
                        <div className='flex items-center gap-1.5'>
                          <span style={{ color: statusConfig.color }}>
                            {statusConfig.icon}
                          </span>
                          <span
                            className='text-xs font-semibold'
                            style={{ color: statusConfig.color }}
                          >
                            {statusConfig.label}
                          </span>
                        </div>

                        {/* Matched user-agent */}
                        <div className='text-xs text-muted-foreground min-w-[100px] text-right'>
                          {bot.matchedUserAgent
                            ? bot.matchedUserAgent === '*'
                              ? 'via wildcard (*)'
                              : `via ${bot.matchedUserAgent}`
                            : 'no match'}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className='flex flex-wrap gap-4 mt-6 pt-4 border-t border-border/60'>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className='flex items-center gap-1.5'>
            <span style={{ color: config.color }}>{config.icon}</span>
            <span className='text-xs text-muted-foreground'>
              {config.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
